import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { getLabRequestIntake, retryRun } from '../api/intake-client';
import { uploadBatch } from '../api/upload-batch';
import { isDeskInFlight, type DeskIntakeAsk } from '../lib/desk-intake-view';
import type { IntakeUploadResponse, LabRequestIntake, LabRequestIntakeEntry } from '../types';
import { intakeInboxKey } from './use-intake-inbox';
import { INTAKE_MUTATION_META, INTAKE_POLL_MS, INTAKE_QUERY_META, intakeQueryRetry } from './use-intake-run-status';

/** Every lab-desk read of one patient starts with this key, so an upload refreshes all of them. */
export const deskIntakePatientKey = (patientId: string) => ['intake-lab-requests', patientId] as const;
const deskIntakeKey = (ask: DeskIntakeAsk) => [...deskIntakePatientKey(ask.patientId), ask.labRequestIds] as const;

/** The server moved the request on (Result received, Patient G2 / G4): confirmed, or failed and filed. */
const settledKey = (i: LabRequestIntake | null) =>
  i && (i.status === 'Completed' || (i.status === 'Failed' && i.patientDocumentId)) ? 'settled' : 'open';

/** Review happens on another page, so a run waiting for review is checked every 30 s. */
export const DESK_REVIEW_POLL_MS = 30_000;

/** A failed run is filed (attached, Result received) by a server sweep within about 2 minutes. */
const awaitsFiling = (i: LabRequestIntake | null) => i?.status === 'Failed' && !i.patientDocumentId;

/**
 * 3 s while a file is read; 30 s while one waits for review (someone may confirm it) or a failed one waits to be
 * filed; else no poll.
 */
export function deskRefetchInterval(data: LabRequestIntakeEntry[] | undefined): number | false {
  if (data?.some((e) => isDeskInFlight(e.intake))) return INTAKE_POLL_MS;
  if (data?.some((e) => e.intake?.status === 'AwaitingReview' || awaitsFiling(e.intake))) return DESK_REVIEW_POLL_MS;
  return false;
}

/**
 * The latest lab-desk run of each request on the page (PROD-3204, PM slice 4), one read per patient. A read polls
 * while one of its requests is being read or waits (`deskRefetchInterval`). When a run settles (confirmed, or failed
 * and filed), the server has moved the request to Result received, so the desk list is read again.
 */
export function useDeskIntake(asks: DeskIntakeAsk[], enabled: boolean): Map<string, LabRequestIntake | null> {
  const queryClient = useQueryClient();
  const results = useQueries({
    queries: asks.map((ask) => ({
      queryKey: deskIntakeKey(ask),
      queryFn: ({ signal }: { signal: AbortSignal }) => getLabRequestIntake(ask.patientId, ask.labRequestIds, signal),
      enabled,
      retry: intakeQueryRetry,
      meta: INTAKE_QUERY_META,
      refetchInterval: (q: { state: { data: LabRequestIntakeEntry[] | undefined } }) => deskRefetchInterval(q.state.data),
    })),
  });

  const byRequest = new Map<string, LabRequestIntake | null>();
  for (const r of results) for (const e of r.data ?? []) byRequest.set(e.labRequestId, e.intake);
  const signature = [...byRequest].map(([id, i]) => `${id}=${i ? `${i.runId}:${settledKey(i)}` : ''}`).join('|');

  const seen = useRef(new Map<string, string>());
  useEffect(() => {
    let settled = false;
    for (const part of signature ? signature.split('|') : []) {
      const [id, now] = part.split('=');
      const before = seen.current.get(id);
      if (before !== undefined && before !== now && now.endsWith(':settled')) settled = true;
      seen.current.set(id, now);
    }
    if (settled) void queryClient.invalidateQueries({ queryKey: ['lab-orders'] });
  }, [signature, queryClient]);

  return byRequest;
}

export interface DeskUploadVars {
  patientId: string;
  file: File;
  /** The requests this report covers (the batch context). */
  labRequestIds: string[];
}

/** Uploads one report from the lab desk, naming the requests it covers, and reports progress (0–1). */
export function useUploadDeskReport() {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<number | null>(null);
  const mutation = useMutation<IntakeUploadResponse, Error, DeskUploadVars>({
    mutationFn: ({ patientId, file, labRequestIds }) => {
      setProgress(0);
      return uploadBatch(patientId, [file], { onProgress: setProgress, context: { labRequestIds } });
    },
    meta: INTAKE_MUTATION_META,
    onSettled: () => setProgress(null),
    onSuccess: (_data, { patientId }) => {
      void queryClient.invalidateQueries({ queryKey: deskIntakePatientKey(patientId) });
      void queryClient.invalidateQueries({ queryKey: intakeInboxKey(patientId) });
    },
  });
  return { ...mutation, progress };
}

/** "Read again" after the reading service did not answer (N7). */
export function useRetryDeskRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, runId }: { patientId: string; runId: string }) => retryRun(patientId, runId),
    meta: INTAKE_MUTATION_META,
    onSettled: (_d, _e, { patientId }) => queryClient.invalidateQueries({ queryKey: deskIntakePatientKey(patientId) }),
  });
}
